import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';
import { OpDropdown } from 'new-components/DLS/OpDropdown/OpDropdown';

import { CheckboxList } from './CheckboxList';
import { RadioList } from './RadioList';

import './SelectFilter.scss';

interface CheckboxFilter {
  allowMultiple: true;
  options: ComponentProps<typeof CheckboxList>['options'];
}

interface RadioFilter {
  allowMultiple: false;
  options: ComponentProps<typeof RadioList>['options'];
}

type SelectFilterProps = {
  onChange: (value?: string[]) => void;
  defaultValue?: string[];
  testId?: string;
  gtm?: string;
  disabled?: boolean;
} & (CheckboxFilter | RadioFilter);

export const SelectFilter = ({
  defaultValue,
  options = [],
  onChange,
  allowMultiple,
  testId,
  gtm,
  disabled,
}: SelectFilterProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [selectValue, setSelectValue] = useState<string[]>(defaultValue || []);

  const handleSelectClear = () => {
    // Reset the select value
    setSelectValue([]);

    // Reset the table state filter
    onChange(undefined);
  };

  // Determine the dropdown render to use
  const popupRender = () => {
    return allowMultiple ? (
      <CheckboxList
        selectValue={selectValue}
        setSelectValue={setSelectValue}
        onChange={onChange}
        options={options}
      />
    ) : (
      <RadioList
        selectValue={selectValue}
        setSelectValue={setSelectValue}
        onChange={onChange}
        options={options}
      />
    );
  };

  return (
    <OpDropdown
      popupRender={popupRender}
      trigger={['click']}
      open={open}
      disabled={disabled}
      onOpenChange={(flag, { source }) => {
        // Ensures clicks within children rendered don't close the dropdown
        if (source === 'trigger') {
          setOpen(flag);
        }
      }}
    >
      <OpSelect
        className="select-filter"
        gtm={gtm && `${gtm}-select-filter`}
        testId={testId}
        data-testid={'select-filter'}
        size="small"
        allowClear
        disabled={disabled}
        showSearch={false}
        placeholder={t('Filter')}
        onClear={handleSelectClear}
        value={
          allowMultiple
            ? selectValue.length
              ? `${selectValue.length} selected`
              : undefined
            : selectValue[0]
        }
        open={false}
        options={options} // The checkbox/radio options both conform to the select options
      />
    </OpDropdown>
  );
};
